import { type ReactElement, useState } from 'react'
import {
  IoPersonOutline,
  IoPersonCircleOutline,
  IoShieldCheckmarkOutline,
  IoNotificationsOutline,
  IoHelpCircleOutline,
  IoChatbubbleEllipsesOutline,
  IoLogOutOutline,
  IoChevronForward,
  IoStarOutline,
  IoMailOutline,
  IoCardOutline,
  IoMoonOutline,
  IoSunnyOutline,
  IoCloseOutline,
  IoMedicalOutline,
  IoCalendarOutline,
  IoDocumentTextOutline,
  IoShieldOutline,
  IoInformationCircleOutline,
  IoToggleOutline,
} from 'react-icons/io5'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useUIStore, useAuthStore } from '../stores'
import { authService } from '../services/api/auth'

type ModalType =
  | 'account'
  | 'professional'
  | 'availability'
  | 'security'
  | 'notifications'
  | 'help'
  | 'contact'
  | null

const NOTIFICATIONS_KEY = 'medisync-notifications'
const AVAILABILITY_KEY = 'medisync-availability'

function getNotificationPrefs() {
  try {
    const s = localStorage.getItem(NOTIFICATIONS_KEY)
    if (s) return JSON.parse(s) as { appointments: boolean; messages: boolean; reminders: boolean; updates: boolean }
  } catch {}
  return { appointments: true, messages: true, reminders: true, updates: false }
}

function getAvailabilityPrefs() {
  try {
    const s = localStorage.getItem(AVAILABILITY_KEY)
    if (s) return JSON.parse(s) as { monday: boolean; tuesday: boolean; wednesday: boolean; thursday: boolean; friday: boolean; saturday: boolean; sunday: boolean }
  } catch {}
  return { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }
}

export default function Profile() {
  const navigate = useNavigate()
  const { theme, toggleTheme } = useUIStore()
  const { user, logout: logoutStore, refreshToken, updateUser } = useAuthStore()
  const [modal, setModal] = useState<ModalType>(null)
  const [notifPrefs, setNotifPrefs] = useState(getNotificationPrefs)

  const handleAction = async (action: string) => {
    switch (action) {
      case 'account':
        setModal('account')
        break
      case 'professional':
        setModal('professional')
        break
      case 'availability':
        setModal('availability')
        break
      case 'security':
        setModal('security')
        break
      case 'theme':
        const newTheme = theme === 'dark' ? 'light' : 'dark'
        toggleTheme()
        toast.success(
          `Thème ${newTheme === 'light' ? 'clair' : 'sombre'} activé`
        )
        break
      case 'notifications':
        setModal('notifications')
        break
      case 'help':
        setModal('help')
        break
      case 'contact':
        setModal('contact')
        break
      case 'logout':
        if (!window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?'))
          return
        try {
          if (refreshToken) {
            await authService.logout(refreshToken)
          }
        } catch {
          // continue to clear local state even if API fails
        }
        logoutStore()
        toast.success('Déconnexion réussie')
        navigate('/login', { replace: true })
        break
    }
  }

  if (!user) {
    return null
  }

  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email

  const appVersion = '1.0.0'

  return (
    <div className='min-h-full'>
      <div className='p-6 lg:p-8 max-w-5xl mx-auto'>
        {/* Header Centré */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className='text-center mb-8'
        >
          <h1 className='text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-primary-400 via-blue-400 to-green-400 bg-clip-text text-transparent'>
            Profil
          </h1>
          <p className='text-gray-600 dark:text-gray-400 text-lg'>
            Gérez vos paramètres et préférences
          </p>
        </motion.div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className='bg-white dark:bg-dark-50 rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg mb-8'
        >
          <div className='flex flex-col items-center text-center space-y-6'>
            <div className='relative'>
              <div className='flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-primary-500/20 to-blue-500/20 border-2 border-primary-500/30 shadow-lg'>
                <IoPersonOutline className='text-5xl text-primary-400' />
              </div>
              <div className='absolute -bottom-1 -right-1 bg-green-500 h-6 w-6 rounded-full border-2 border-white dark:border-dark-50 flex items-center justify-center'>
                <div className='h-3 w-3 bg-green-500 rounded-full' />
              </div>
            </div>

            <div>
              <h2 className='text-2xl font-bold text-gray-100 mb-2'>
                {displayName}
              </h2>
              <div className='inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full px-4 py-1.5 mb-3'>
                <IoStarOutline className='text-yellow-400 text-sm' />
                <span className='text-yellow-400 text-sm font-semibold'>
                  Infirmier(ère)
                </span>
              </div>
              <div className='space-y-2 mt-4'>
                <div className='flex items-center justify-center space-x-2 text-gray-600 dark:text-gray-400'>
                  <IoMailOutline className='text-sm' />
                  <span className='text-sm'>{user.email}</span>
                </div>
                {user.licenseNumber && (
                  <div className='flex items-center justify-center space-x-2 text-gray-600 dark:text-gray-400'>
                    <IoCardOutline className='text-sm' />
                    <span className='text-sm'>
                      N° ordre: {user.licenseNumber}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Sections */}
        <div className='space-y-6'>
          {/* Mon Compte */}
          <Section
            title='Mon Compte'
            items={[
              {
                icon: <IoPersonCircleOutline className='text-xl' />,
                label: 'Informations personnelles',
                description: 'Nom, email',
                color: 'blue',
                onClick: () => handleAction('account'),
              },
              {
                icon: <IoMedicalOutline className='text-xl' />,
                label: 'Informations professionnelles',
                description: 'RPPS, spécialité, cabinet',
                color: 'green',
                onClick: () => handleAction('professional'),
              },
              {
                icon: <IoCalendarOutline className='text-xl' />,
                label: 'Disponibilités',
                description: 'Horaires de travail',
                color: 'primary',
                onClick: () => handleAction('availability'),
              },
            ]}
          />

          {/* Préférences */}
          <Section
            title='Préférences'
            items={[
              {
                icon:
                  theme === 'dark' ? (
                    <IoSunnyOutline className='text-xl' />
                  ) : (
                    <IoMoonOutline className='text-xl' />
                  ),
                label: `Thème ${theme === 'dark' ? 'clair' : 'sombre'}`,
                color: 'primary',
                onClick: () => handleAction('theme'),
              },
              {
                icon: <IoNotificationsOutline className='text-xl' />,
                label: 'Notifications',
                description: `${Object.values(notifPrefs).filter(Boolean).length} type(s) activé(s)`,
                color: 'orange',
                onClick: () => handleAction('notifications'),
              },
            ]}
          />

          {/* Sécurité */}
          <Section
            title='Sécurité & Mot de passe'
            items={[
              {
                icon: <IoShieldCheckmarkOutline className='text-xl' />,
                label: 'Modifier le mot de passe',
                color: 'green',
                onClick: () => handleAction('security'),
              },
            ]}
          />

          {/* Aide & Support */}
          <Section
            title='Aide & Support'
            items={[
              {
                icon: <IoHelpCircleOutline className='text-xl' />,
                label: "Centre d'aide",
                color: 'blue',
                onClick: () => handleAction('help'),
              },
              {
                icon: <IoChatbubbleEllipsesOutline className='text-xl' />,
                label: 'Nous contacter',
                color: 'green',
                onClick: () => handleAction('contact'),
              },
            ]}
          />

          {/* Informations légales */}
          <Section
            title='Informations légales'
            items={[
              {
                icon: <IoDocumentTextOutline className='text-xl' />,
                label: "Conditions d'utilisation",
                color: 'blue',
                onClick: () => navigate('/legal?tab=cgu'),
              },
              {
                icon: <IoShieldOutline className='text-xl' />,
                label: 'Politique de confidentialité',
                color: 'green',
                onClick: () => navigate('/legal?tab=privacy'),
              },
              {
                icon: <IoInformationCircleOutline className='text-xl' />,
                label: 'Mentions légales',
                color: 'primary',
                onClick: () => navigate('/legal?tab=legal'),
              },
            ]}
          />

          {/* Version */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-lg overflow-hidden'
          >
            <div className='flex items-center justify-between px-6 py-4'>
              <div className='flex items-center gap-4'>
                <span className='p-2 rounded-lg text-gray-400 bg-gray-500/10'>
                  <IoToggleOutline className='text-xl' />
                </span>
                <span className='text-base font-medium text-gray-700 dark:text-gray-300'>Version</span>
              </div>
              <span className='text-sm text-gray-400'>{appVersion}</span>
            </div>
          </motion.div>

          {/* Déconnexion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-lg overflow-hidden'
          >
            <Item
              icon={<IoLogOutOutline className='text-xl' />}
              label='Déconnexion'
              danger
              onClick={() => handleAction('logout')}
            />
          </motion.div>

          {/* Footer */}
          <p className='text-center text-xs text-gray-400 dark:text-gray-600 pb-4'>
            MediSync © 2026 — Fait avec soin pour les soignants
          </p>
        </div>

        {/* Modals */}
        <AnimatePresence>
          {modal === 'account' && user && (
            <AccountModal
              user={user}
              onClose={() => setModal(null)}
              onSaved={data => {
                updateUser(data)
                setModal(null)
                toast.success('Profil mis à jour')
              }}
            />
          )}
          {modal === 'professional' && (
            <ProfessionalModal onClose={() => setModal(null)} />
          )}
          {modal === 'availability' && (
            <AvailabilityModal onClose={() => setModal(null)} />
          )}
          {modal === 'security' && (
            <SecurityModal onClose={() => setModal(null)} />
          )}
          {modal === 'notifications' && (
            <NotificationsModal
              prefs={notifPrefs}
              onSave={prefs => {
                setNotifPrefs(prefs)
                localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(prefs))
                setModal(null)
                toast.success('Préférences enregistrées')
              }}
              onClose={() => setModal(null)}
            />
          )}
          {modal === 'help' && <HelpModal onClose={() => setModal(null)} />}
          {modal === 'contact' && (
            <ContactModal onClose={() => setModal(null)} />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function Section({
  title,
  items,
}: {
  title: string
  items: {
    icon: ReactElement
    label: string
    description?: string
    color: 'blue' | 'green' | 'primary' | 'orange' | 'red'
    onClick: () => void
  }[]
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className='space-y-3'
    >
      <h3 className='text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 px-2'>
        {title}
      </h3>
      <div className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-lg overflow-hidden divide-y divide-gray-200 dark:divide-gray-700/50'>
        {items.map((item, index) => (
          <Item
            key={item.label}
            icon={item.icon}
            label={item.label}
            description={item.description}
            color={item.color}
            onClick={item.onClick}
            isLast={index === items.length - 1}
          />
        ))}
      </div>
    </motion.section>
  )
}

function Item({
  icon,
  label,
  description,
  danger,
  color = 'blue',
  onClick,
  isLast = false,
}: {
  icon: ReactElement
  label: string
  description?: string
  danger?: boolean
  color?: 'blue' | 'green' | 'primary' | 'orange' | 'red'
  onClick?: () => void
  isLast?: boolean
}) {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    primary: 'text-primary-400 bg-primary-500/10',
    orange: 'text-orange-400 bg-orange-500/10',
    red: 'text-red-400 bg-red-500/10',
  }

  return (
    <motion.button
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`flex items-center justify-between w-full px-6 py-4 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-all duration-200 ${
        isLast ? '' : 'border-b border-gray-200 dark:border-gray-700/50'
      } ${danger ? 'text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10' : 'text-gray-900 dark:text-gray-100'}`}
    >
      <span className='flex items-center gap-4'>
        <span
          className={`p-2 rounded-lg ${
            danger ? 'text-red-400 bg-red-500/10' : colorClasses[color]
          }`}
        >
          {icon}
        </span>
        <span className='text-left'>
          <span className='block text-base font-medium'>{label}</span>
          {description && (
            <span className='block text-xs text-gray-400 mt-0.5'>{description}</span>
          )}
        </span>
      </span>
      <IoChevronForward
        className={`text-lg ${danger ? 'text-red-400/50' : 'text-gray-400'}`}
      />
    </motion.button>
  )
}

function ModalOverlay({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4'
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col'
      >
        <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
            {title}
          </h3>
          <button
            type='button'
            onClick={onClose}
            className='p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors'
          >
            <IoCloseOutline className='text-xl' />
          </button>
        </div>
        <div className='flex-1 overflow-y-auto p-4'>{children}</div>
      </motion.div>
    </motion.div>
  )
}

function AccountModal({
  user,
  onClose,
  onSaved,
}: {
  user: { firstName: string; lastName: string; email: string }
  onClose: () => void
  onSaved: (data: {
    firstName: string
    lastName: string
    email: string
  }) => void
}) {
  const [firstName, setFirstName] = useState(user.firstName)
  const [lastName, setLastName] = useState(user.lastName)
  const [email, setEmail] = useState(user.email)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast.error('Tous les champs sont requis')
      return
    }
    setLoading(true)
    try {
      const res = await authService.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
      })
      if (res.success && res.data) {
        onSaved({
          firstName: res.data.firstName,
          lastName: res.data.lastName,
          email: res.data.email,
        })
      } else {
        toast.error(res.message || 'Impossible de mettre à jour le profil')
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Erreur'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay title='Informations personnelles' onClose={onClose}>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Prénom
          </label>
          <input
            type='text'
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            className='input-field w-full'
            placeholder='Prénom'
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Nom
          </label>
          <input
            type='text'
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            className='input-field w-full'
            placeholder='Nom'
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Email
          </label>
          <input
            type='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            className='input-field w-full'
            placeholder='email@exemple.com'
          />
        </div>
        <div className='flex gap-2 pt-2'>
          <button
            type='button'
            onClick={onClose}
            className='btn-secondary flex-1'
          >
            Annuler
          </button>
          <button
            type='submit'
            disabled={loading}
            className='btn-primary flex-1'
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  )
}

function ProfessionalModal({ onClose }: { onClose: () => void }) {
  const PROF_KEY = 'medisync-professional'
  const stored = (() => {
    try { return JSON.parse(localStorage.getItem(PROF_KEY) ?? '{}') } catch { return {} }
  })()
  const [rpps, setRpps] = useState<string>(stored.rpps ?? '')
  const [speciality, setSpeciality] = useState<string>(stored.speciality ?? '')
  const [cabinet, setCabinet] = useState<string>(stored.cabinet ?? '')
  const [loading, setLoading] = useState(false)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const rppsTrimmed = rpps.trim()
    if (rppsTrimmed && !/^\d{11}$/.test(rppsTrimmed)) {
      toast.error('Le numéro RPPS doit contenir exactement 11 chiffres')
      return
    }
    setLoading(true)
    try {
      await authService.updateProfile({
        ...(rppsTrimmed ? { rppsNumber: rppsTrimmed } : {}),
        specialization: speciality.trim() || undefined,
        cabinetName: cabinet.trim() || undefined,
      })
      localStorage.setItem(PROF_KEY, JSON.stringify({ rpps, speciality, cabinet }))
      toast.success('Informations professionnelles enregistrées')
      onClose()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Erreur'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay title='Informations professionnelles' onClose={onClose}>
      <form onSubmit={handleSave} className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Numéro RPPS
          </label>
          <input
            type='text'
            value={rpps}
            onChange={e => setRpps(e.target.value)}
            className='input-field w-full'
            placeholder='Ex: 10003041234'
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Spécialité
          </label>
          <input
            type='text'
            value={speciality}
            onChange={e => setSpeciality(e.target.value)}
            className='input-field w-full'
            placeholder='Ex: Infirmier libéral'
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Cabinet / Structure
          </label>
          <input
            type='text'
            value={cabinet}
            onChange={e => setCabinet(e.target.value)}
            className='input-field w-full'
            placeholder='Ex: Cabinet infirmier du centre'
          />
        </div>
        <div className='flex gap-2 pt-2'>
          <button type='button' onClick={onClose} className='btn-secondary flex-1'>Annuler</button>
          <button type='submit' className='btn-primary flex-1' disabled={loading}>
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  )
}

const DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
] as const

function AvailabilityModal({ onClose }: { onClose: () => void }) {
  const [days, setDays] = useState(getAvailabilityPrefs)

  const toggle = (key: keyof typeof days) => {
    setDays(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSave = () => {
    localStorage.setItem(AVAILABILITY_KEY, JSON.stringify(days))
    toast.success('Disponibilités enregistrées')
    onClose()
  }

  return (
    <ModalOverlay title='Disponibilités' onClose={onClose}>
      <div className='space-y-3'>
        <p className='text-sm text-gray-500 dark:text-gray-400 mb-4'>
          Sélectionnez vos jours de travail habituels.
        </p>
        {DAYS.map(({ key, label }) => (
          <label key={key} className='flex items-center justify-between gap-4 cursor-pointer px-1 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50'>
            <span className='text-gray-700 dark:text-gray-300 font-medium'>{label}</span>
            <button
              type='button'
              onClick={() => toggle(key)}
              className={`relative w-11 h-6 rounded-full transition-colors ${days[key] ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${days[key] ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </label>
        ))}
        <div className='flex gap-2 pt-4'>
          <button type='button' onClick={onClose} className='btn-secondary flex-1'>Annuler</button>
          <button type='button' onClick={handleSave} className='btn-primary flex-1'>Enregistrer</button>
        </div>
      </div>
    </ModalOverlay>
  )
}

function SecurityModal({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      toast.error('Les deux mots de passe ne correspondent pas')
      return
    }
    if (newPassword.length < 8) {
      toast.error('Le nouveau mot de passe doit faire au moins 8 caractères')
      return
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('Le nouveau mot de passe doit contenir au moins une majuscule')
      return
    }
    if (!/[a-z]/.test(newPassword)) {
      toast.error('Le nouveau mot de passe doit contenir au moins une minuscule')
      return
    }
    if (!/[0-9]/.test(newPassword)) {
      toast.error('Le nouveau mot de passe doit contenir au moins un chiffre')
      return
    }
    // eslint-disable-next-line no-useless-escape
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>?]/.test(newPassword)) {
      toast.error('Le nouveau mot de passe doit contenir au moins un caractère spécial')
      return
    }
    setLoading(true)
    try {
      await authService.changePassword({
        currentPassword,
        newPassword,
        confirmPassword,
      })
      toast.success('Mot de passe modifié')
      onClose()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Erreur'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay title='Modifier le mot de passe' onClose={onClose}>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Mot de passe actuel
          </label>
          <input
            type='password'
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            className='input-field w-full'
            placeholder='••••••••'
            required
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Nouveau mot de passe
          </label>
          <input
            type='password'
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            className='input-field w-full'
            placeholder='••••••••'
            required
            minLength={8}
          />
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
            Min. 8 caractères, une majuscule, une minuscule, un chiffre, un
            caractère spécial
          </p>
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Confirmer le mot de passe
          </label>
          <input
            type='password'
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            className='input-field w-full'
            placeholder='••••••••'
            required
          />
        </div>
        <div className='flex gap-2 pt-2'>
          <button
            type='button'
            onClick={onClose}
            className='btn-secondary flex-1'
          >
            Annuler
          </button>
          <button
            type='submit'
            disabled={loading}
            className='btn-primary flex-1'
          >
            {loading ? 'Modification...' : 'Modifier le mot de passe'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  )
}

const NOTIF_ITEMS = [
  { key: 'appointments' as const, label: 'Rendez-vous', description: 'Rappels de visites' },
  { key: 'messages' as const, label: 'Messages', description: 'Nouveaux messages' },
  { key: 'reminders' as const, label: 'Rappels', description: 'Tâches et suivis' },
  { key: 'updates' as const, label: 'Mises à jour', description: "Nouveautés de l'app" },
]

function NotificationsModal({
  prefs,
  onSave,
  onClose,
}: {
  prefs: { appointments: boolean; messages: boolean; reminders: boolean; updates: boolean }
  onSave: (prefs: { appointments: boolean; messages: boolean; reminders: boolean; updates: boolean }) => void
  onClose: () => void
}) {
  const [local, setLocal] = useState(prefs)

  const toggle = (key: keyof typeof local) => {
    setLocal(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <ModalOverlay title='Notifications' onClose={onClose}>
      <div className='space-y-3'>
        {NOTIF_ITEMS.map(({ key, label, description }) => (
          <label key={key} className='flex items-center justify-between gap-4 cursor-pointer px-1 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50'>
            <span>
              <span className='block text-gray-700 dark:text-gray-300 font-medium'>{label}</span>
              <span className='block text-xs text-gray-400 mt-0.5'>{description}</span>
            </span>
            <button
              type='button'
              onClick={() => toggle(key)}
              className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${local[key] ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${local[key] ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </label>
        ))}
        <div className='flex gap-2 pt-4'>
          <button
            type='button'
            onClick={onClose}
            className='btn-secondary flex-1'
          >
            Annuler
          </button>
          <button
            type='button'
            onClick={() => onSave(local)}
            className='btn-primary flex-1'
          >
            Enregistrer
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalOverlay title="Centre d'aide" onClose={onClose}>
      <div className='space-y-4 text-gray-700 dark:text-gray-300 text-sm'>
        <p>
          <strong>MediSync</strong> vous permet de gérer vos patients,
          rendez-vous et messages.
        </p>
        <p>
          <strong>Messagerie :</strong> utilisez la messagerie pour échanger
          avec les autres infirmiers.
        </p>
        <p>
          <strong>Planning :</strong> consultez et créez des rendez-vous dans
          l'onglet Planning.
        </p>
        <p>
          <strong>Dossiers patients :</strong> les dossiers et documents sont
          accessibles depuis la liste des patients.
        </p>
        <p className='text-gray-500 dark:text-gray-400 pt-2'>
          Pour toute question technique, contactez le support (voir « Nous
          contacter »).
        </p>
        <button
          type='button'
          onClick={onClose}
          className='btn-primary w-full mt-4'
        >
          Fermer
        </button>
      </div>
    </ModalOverlay>
  )
}

function ContactModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalOverlay title='Nous contacter' onClose={onClose}>
      <div className='space-y-4 text-gray-700 dark:text-gray-300 text-sm'>
        <p>
          Pour toute demande ou signalement, envoyez un email à l'adresse
          ci-dessous.
        </p>
        <a
          href='mailto:support@medisync.com'
          className='inline-flex items-center gap-2 text-primary-400 hover:underline'
        >
          <IoMailOutline className='text-lg' />
          support@medisync.com
        </a>
        <button
          type='button'
          onClick={onClose}
          className='btn-primary w-full mt-4'
        >
          Fermer
        </button>
      </div>
    </ModalOverlay>
  )
}
